import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import type { CapabilityKey } from '@brightlocal/shared';

import { isCapabilityEnabled } from './getCapability';
import { revalidateCustomerCapabilities } from '../customerCapabilitiesQuery';

interface RequireCustomerCapabilityParams {
  /** Router-context query client */
  queryClient: QueryClient;
  /** On/off capability the route needs */
  key: CapabilityKey;
  /** Where to send the customer when their plan doesn't include the capability */
  fallbackRoute: string;
  /** Attempted internal href (`pathname + search + hash`), kept for the return after a session-expired login */
  attemptedHref: string;
}

/**
 * Route guard for TanStack Router `beforeLoad`: redirects to `fallbackRoute` unless the customer's
 * plan enables the capability. Refreshes the capabilities first when they are stale, so the guard
 * never decides from stale access; it fails closed when no read has succeeded.
 */
export const requireCustomerCapability = async ({
  queryClient,
  key,
  fallbackRoute,
  attemptedHref
}: RequireCustomerCapabilityParams) => {
  const capabilities = await revalidateCustomerCapabilities({ queryClient, attemptedHref });

  if (isCapabilityEnabled({ capabilities, key })) {
    return;
  }

  throw redirect({ to: fallbackRoute });
};
